import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { LoadingComponent, useExecutionDataView } from "@gooddata/sdk-ui";
import { useAppContext } from "../../contexts/AppContext";
import FiltersContext from "../../contexts/Filters";
import HighchartsComponent from "../HighchartsComponent";
import * as Md from "../../md/full";
import { newJsonPivot } from "../../utils/jsonPivot";
import MeasuresContext from "../../contexts/Measures";
import { operations } from "./CarSelectorConfigModal";
import { BiSync } from "react-icons/bi";
import { calculateAvg } from "../../utils/calculationUtils";

const getHighlightedMarkerSvg = (symbol, seriesColor, highlightColor) => {
    const width = 18,
        height = 18;
    let shapeSvg = "";

    switch (symbol) {
        case "diamond":
            shapeSvg = `<path d="M 8 2 L 14 8 L 8 14 L 2 8 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
            break;
        case "square":
            shapeSvg = `<rect x="2" y="2" width="12" height="12" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
            break;
        case "triangle":
            shapeSvg = `<path d="M 8 2 L 15 14 L 1 14 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
            break;
        case "triangle-down":
            shapeSvg = `<path d="M 8 14 L 15 2 L 1 2 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
            break;
        case "triangle-up":
            shapeSvg = `<path d="M 8 14 L 15 2 L 1 2 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
            break;
        case "circle":
        default:
            shapeSvg = `<circle cx="8" cy="8" r="7" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
            break;
    }

    return `
            <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
                ${shapeSvg}
            </svg>
        `.trim();
};

const symbols = ["circle", "diamond", "square", "triangle", "triangle-down"];

/**
 *
 * @param {*} data: List of cust kpi values for each vehicle
 * @returns
 */
const MakeModelOverview = ({ seriesBy, filters, footnote, title, kpiSettings, selectedVehicles, kpi, ...props }) => {
    const id = "synthesis-graph";
    const { getParamsByCustID, showListPrices: showListPricesContext } = useAppContext();
    const { getFirstFilterValue } = useContext(FiltersContext);
    const { selectedKpi } = useContext(MeasuresContext);

    const [showListPrice, setShowListPrice] = useState(true);
    const [showScatter, setShowScatter] = useState(true);
    const [data, setData] = useState([]);
    const [listPrices, setListPrices] = useState([]);
    const [currentFilter, setCurrentFilter] = useState(filters);
    const [rolledForwardDataPoints, setRolledForwardDataPoints] = useState(new Set());
    const { customFields } = props;

    useEffect(() => {
        setCurrentFilter(filters);
    }, [filters]);

    const { result, status } = useExecutionDataView(
        {
            execution: {
                seriesBy: seriesBy,
                slicesBy: [Md.CustId, Md.VersionStd, Md.Rolledforward, Md.Version],
                filters: currentFilter,
            },
        },
        [currentFilter, seriesBy],
    );

    const processCustomFields = useCallback(
        (parsedData) => {
            if (customFields && parsedData) {
                return parsedData.map((versionObj) => {
                    const custIds = Object.keys(versionObj).filter((key) => key !== "versionStd");
                    const customFieldsObj = customFields.reduce((acc, field) => {
                        const coeffs = field.players.filter((player) => custIds.includes(player)).map((player) => versionObj[player]);
                        return {
                            ...acc,
                            [field.fieldName]: operations.calculate(coeffs, field.operation, (value) => (selectedKpi.index === 24 ? Number(value.toFixed(2)) : Math.round(value))),
                        };
                    }, {});
                    return {
                        ...versionObj,
                        ...customFieldsObj,
                    };
                });
            }
            return parsedData;
        },
        [customFields, selectedKpi.index],
    );

    useEffect(() => {
        if (result) {
            const seriesData = result
                ?.data()
                .series()
                .toArray()
                .find((serie) => serie.measureTitle() === seriesBy[selectedKpi.index].measure.localIdentifier);
            const dataPoints = seriesData
                ?.dataPoints()
                .reduce((acc, dp) => {
                    if (dp.rawValue !== null) {
                        const val = Number(dp.rawValue);
                        const custId = dp.sliceDesc.sliceTitles()[0];
                        const versionStd = dp.sliceDesc.sliceTitles()[1];
                        const i = acc.findIndex((o) => o.versionStd === versionStd && o.custId === custId);
                        if (i !== -1) {
                            acc[i].values.push(val);
                        } else {
                            let o = {
                                custId: custId,
                                versionStd: dp.sliceDesc.sliceTitles()[1],
                                rolledforward: dp.sliceDesc.sliceTitles()[2],
                                values: [val],
                            };
                            acc.push(o);
                        }
                    }
                    return acc;
                }, [])
                .map((o) => ({
                    ...o,
                    value: selectedKpi.roundValue(calculateAvg(o.values)),
                }));
            // .map((dp) =>
            //     Object.freeze({
            //         value: Number(dp.rawValue) === 0 ? null : selectedKpi.index === 24 ? Number((Number(dp.rawValue) * 100).toFixed(2)) : Math.round(Number(dp.rawValue)),
            //         custId: dp.sliceDesc.sliceTitles()[0],
            //         versionStd: dp.sliceDesc.sliceTitles()[1],
            //         rolledforward: dp.sliceDesc.sliceTitles()[2],
            //     }),
            // );
            if (dataPoints) {
                setRolledForwardDataPoints(new Set(dataPoints.filter((dp) => dp.rolledforward === "Yes").map((dp) => `${dp.versionStd}|${dp.custId}`)));
                let parsedData = newJsonPivot(dataPoints, { row: "versionStd", column: "custId", value: "value" });
                setData(processCustomFields(parsedData));
            } else {
                setData([]);
            }
            const lpdata = result
                ?.data()
                .series()
                .toArray()
                .find((serie) => serie.measureTitle() === seriesBy[1].measure.localIdentifier)
                ?.dataPoints()
                .map((dp) =>
                    Object.freeze({
                        value: Math.round(Number(dp.rawValue)),
                        custId: dp.sliceDesc.sliceTitles()[0],
                        versionStd: dp.sliceDesc.sliceTitles()[1],
                    }),
                );
            if (lpdata) {
                let parsedData = newJsonPivot(lpdata, { row: "versionStd", column: "custId", value: "value" });
                setListPrices(processCustomFields(parsedData));
            } else {
                setListPrices([]);
            }
        }
    }, [result, selectedKpi, processCustomFields, seriesBy]);

    const chartData = useMemo(() => {
        if (!data) return [];
        const mappedData = data.map((elem) => {
            const dataValues =
                Object.entries(elem)
                    .filter(([key, value]) => key !== "versionStd" && key !== "make" && value !== null)
                    .map(([, value]) => value)
                    .sort((a, b) => a - b) || [];

            return {
                name: elem.versionStd,
                y: dataValues[0],
                color: "#506e96",
            };
        });
        mappedData.sort((a, b) => b.y - a.y);
        return mappedData;
    }, [data]);

    const listpriceData = useMemo(() => {
        if (!listPrices) return [];
        const mappedData = listPrices.map((elem) => {
            const dataValues = Object.entries(elem)
                .filter(([key, value]) => key !== "versionStd" && key !== "make" && value !== null)
                .map(([, value]) => value)
                .sort((a, b) => a - b);

            return {
                name: elem.versionStd,
                y: dataValues[0],
                color: "#ff7d93",
            };
        });

        mappedData.sort((a, b) => {
            const aIndex = chartData.findIndex((item) => item.name === a.name);
            const bIndex = chartData.findIndex((item) => item.name === b.name);
            return aIndex - bIndex;
        });
        return mappedData;
    }, [listPrices, chartData]);

    const categories = useMemo(() => chartData?.map((item) => item.name) || [], [chartData]);

    const custOfferSeries = useMemo(() => {
        return (data || []).reduce((acc, elem) => {
            const custOffersMatrix = Object.entries(elem).filter(([key, value]) => key !== "versionStd" && key !== "make" && value !== null);
            const xIndex = categories.findIndex((category) => category === elem?.versionStd);
            custOffersMatrix.forEach((custOffer) => {
                const custId = custOffer[0];
                const custObj = getParamsByCustID(custId, getFirstFilterValue("filterCountryId"));
                const custSerie = acc.find((item) => item.id === custId);
                const point = {
                    x: showListPrice ? xIndex - 0.14 : xIndex,
                    y: custOffer[1],
                };
                if (rolledForwardDataPoints.has(`${elem.versionStd}|${custId}`)) {
                    const seriesColor = (custSerie && custSerie.color) || custObj?.hexColor || "#6c757d";
                    const symbol = (custSerie && custSerie.marker.symbol) || symbols[acc.length % symbols.length];
                    const svgIcon = getHighlightedMarkerSvg(symbol, seriesColor, "#BFBFBF");

                    point.marker = {
                        symbol: `url(data:image/svg+xml;base64,${window.btoa(svgIcon)})`,
                        width: 18,
                        height: 18,
                    };
                }
                if (custSerie !== undefined) {
                    custSerie.data.push(point);
                } else {
                    acc.push({
                        type: "scatter",
                        id: custId,
                        name: custObj?.custLabel || custId,
                        color: custObj?.hexColor || "#6c757d",
                        marker: {
                            symbol: symbols[acc.length % symbols.length],
                        },
                        data: [point],
                    });
                }
            });
            return acc;
        }, []);
    }, [categories, data, getFirstFilterValue, getParamsByCustID, rolledForwardDataPoints, showListPrice]);

    useEffect(() => {
        setShowListPrice(showListPricesContext);
    }, [showListPricesContext]);

    const handleSyncData = useCallback(() => {
        const newFilter = currentFilter.slice(0, 6).concat(currentFilter.slice(23));
        setCurrentFilter(newFilter);
    }, [currentFilter]);

    return (
        <div>
            {status === "loading" ? (
                <div className="min-h-[400px] flex items-center justify-center">
                    <LoadingComponent />
                </div>
            ) : (
                <div className="border border-gray-200 shadow rounded mt-1">
                    {!props.widgetUid && data?.length !== selectedVehicles?.length && (
                        <div className="flex items-center gap-2 m-2">
                            <span className="block sm:inline text-steel font-medium">Your filters are not synced with the selected vehicles !</span>
                            <button onClick={handleSyncData} className="px-2 py-1.5 text-sm font-medium bg-indigo-500 text-white rounded-md flex items-center hover:opacity-80">
                                <BiSync className="w-5 h-5" />
                                Sync data
                            </button>
                        </div>
                    )}
                    <HighchartsComponent
                        menuLocation={data?.length !== selectedVehicles?.length ? { top: "-100px" } : {}}
                        widgetProps={{
                            id,
                            filters,
                            section: "car-selector",
                            flag_services: kpiSettings,
                        }}
                        options={{
                            caption:
                                rolledForwardDataPoints && rolledForwardDataPoints.size > 0
                                    ? {
                                          text: '<div style="display: flex; align-items: center; font-size: 10px; color: #666666;"><svg width="16" height="16" viewBox="0 0 16 16" style="margin-right: 4px;"><circle cx="8" cy="8" r="6" fill="#506e96" stroke="#BFBFBF" stroke-width="3"/></svg><span>Gray bordered points are from previous month</span></div>',
                                          useHTML: true,
                                          align: "left",
                                          verticalAlign: "bottom",
                                          y: 15,
                                          x: 10,
                                      }
                                    : {},
                            chart: {
                                height: props.height || (categories.length < 6 ? 450 : 600),
                            },
                            plotOptions: {
                                // series: {
                                // },
                                bar: {
                                    borderRadius: 3.0,
                                    enableMouseTracking: false,
                                    dataLabels: {
                                        enabled: true,
                                        align: "right",
                                        x: -20,
                                        color: "#FFFFFF",
                                        formatter: function () {
                                            return this.y !== 0 ? this.y : null;
                                        },
                                    },
                                },
                                scatter: {
                                    stickyTracking: false,
                                    // findNearestPointBy: 'y',
                                    marker: {
                                        radius: 6,
                                    },
                                },
                            },
                            title: {
                                text: title,
                                align: "center",
                                style: {
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: "#595959",
                                },
                            },
                            tooltip: {
                                snap: 0,
                                formatter: function () {
                                    if (!["Cheapest", "List Price"].includes(this.series.name)) {
                                        const custId = this.series.options.id;
                                        const versionStd = this.point.category;
                                        const isRolledForward = rolledForwardDataPoints.has(`${versionStd}|${custId}`);
                                        let tooltip = `<b>${this.series.name}</b><br/>${this.y}`;
                                        if (isRolledForward) {
                                            tooltip += `<br/><b>(From previous month)</b>`;
                                        }
                                        return tooltip;
                                    } else return `<span>${this.x}</span><br/><em>Adj.rental</em> ${this.y}`;
                                },
                            },
                            series: showListPricesContext
                                ? [
                                      {
                                          name: "Cheapest",
                                          type: "bar",
                                          data: chartData,
                                          color: "#506e96",
                                          yAxis: 0,
                                          pointWidth: chartData.length < 12 ? 30.0 : 25.0,
                                          //   grouping: false,
                                          events: {
                                              legendItemClick: function (event) {
                                                  if (event.target.name === "Cheapest") {
                                                      setShowScatter(!showScatter);
                                                  }
                                                  return true;
                                              },
                                          },
                                      },
                                      {
                                          name: "List Price",
                                          type: "bar",
                                          data: listpriceData,
                                          color: "#ff7d93",
                                          yAxis: 1,
                                          pointWidth: (chartData.length < 12 ? 30.0 : 25.0) * 0.6,
                                          //   grouping: false,
                                          events: {
                                              legendItemClick: function (event) {
                                                  if (event.target.name === "List Price") {
                                                      setShowListPrice(!showListPrice);
                                                  }
                                                  return true;
                                              },
                                          },
                                      },
                                      ...custOfferSeries.map((serie) => ({
                                          ...serie,
                                          visible: showScatter,
                                      })),
                                  ]
                                : [
                                      {
                                          name: "Cheapest",
                                          type: "bar",
                                          data: chartData,
                                          color: "#506e96",
                                          yAxis: 0,
                                          pointWidth: 30.0,
                                      },
                                      ...custOfferSeries,
                                  ],
                            xAxis: {
                                categories: categories,
                                labels: {
                                    style: {
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        color: "#595959",
                                    },
                                },
                            },
                            yAxis: showListPricesContext
                                ? [
                                      {
                                          title: {
                                              text: showScatter ? title?.split("|")[5] : "List Price",
                                              style: {
                                                  color: showScatter ? "#506e96" : "#ff7d93",
                                              },
                                          },
                                          labels: {
                                              style: {
                                                  color: showScatter ? "#506e96" : "#ff7d93",
                                              },
                                          },
                                          alignTicks: false,
                                      },
                                      {
                                          title: {
                                              text: null,
                                          },
                                          labels: {
                                              enabled: false,
                                          },
                                          opposite: true,
                                          alignTicks: false,
                                          gridLineWidth: showScatter ? 0 : 1,
                                          type: "logarithmic",
                                          tickInterval: 1.5,
                                      },
                                  ]
                                : [
                                      {
                                          title: {
                                              text: title?.split("|")[5],
                                              style: {
                                                  color: "#506e96",
                                              },
                                          },
                                          labels: {
                                              style: {
                                                  color: "#506e96",
                                              },
                                          },
                                          alignTicks: false,
                                      },
                                  ],
                            legend: {
                                enabled: true,
                                symbolRadius: 0,
                                symbolHeight: 16,
                                labelFormatter: function () {
                                    if (this.name === "Cheapest") return `<span style="width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;color:#506e96">${this.name}</span>`;
                                    else return `<div style="width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${this.name}</div>`;
                                },
                            },
                        }}
                        {...props}
                    />
                </div>
            )}
            {footnote && <span className="text-xs font-medium">{footnote}</span>}
        </div>
    );
};

export default MakeModelOverview;
